import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { OVIZIA_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${OVIZIA_SERVICE_BASE_URL}/api/v1/admin/templates`;

const DEFAULT_QUERY = {
  page: 0,
  size: 20,
  sort: "name,asc",
  search: "",
  type: null,
  active: null,
};

// changing any of these starts again from the first page
const PAGE_RESETTING_KEYS = ["search", "type", "active", "size", "sort"];

export default function useWebsiteTemplates() {
  const authToken = sessionStorage.getItem("authToken");

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  // error handler
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err.response) {
      const status = err.response.status;

      if (status >= 500) {
        message = "Server error. Please try again later.";
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message = "No response from server. Check your connection.";
    } else {
      message = err.message || message;
    }

    toast.error(message);
    console.error(`${action} error:`, err);
    setError(message);
  };

  // normalize data
  const formatTemplate = (data) => ({
    slug: data.slug,
    name: data.name,
    description: data.description,
    thumbnailUrl: data.thumbnailUrl,
    category: data.category,
    type: data.type,
    tags: data.tags || [],
    sections: data.sections || [],
    active: data.active,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  });

  // change filters / page / size. Filter changes reset to page 0.
  const updateQuery = useCallback((patch) => {
    setQuery((prev) => {
      const resets =
        !("page" in patch) && PAGE_RESETTING_KEYS.some((k) => k in patch);
      const next = { ...prev, ...(resets ? { page: 0 } : {}), ...patch };
      const unchanged = Object.keys(next).every((k) => next[k] === prev[k]);
      return unchanged ? prev : next; // same values -> no refetch
    });
  }, []);

  // refetch the current page (used after create / update / deactivate / reactivate)
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchTemplates = useCallback(
    async (q) => {
      if (!authToken) return;

      const requestId = ++requestIdRef.current; // newer request invalidates older ones
      setLoading(true);
      setError(null);

      try {
        const params = { page: q.page, size: q.size, sort: q.sort };
        if (q.search) params.search = q.search;
        if (q.type) params.type = q.type;
        if (q.active !== null) params.active = q.active;

        const res = await apiClient.get(BASE_URL, { params });
        if (requestId !== requestIdRef.current) return; // stale response

        const { content = [], totalPages = 1, totalElements = 0 } = res.data;

        // current page no longer exists (e.g. last row was deactivated under a filter)
        if (content.length === 0 && q.page > 0 && totalPages > 0) {
          setQuery((prev) => ({ ...prev, page: totalPages - 1 }));
          return;
        }

        setTemplates(content.map(formatTemplate));
        setTotalPages(totalPages);
        setTotalElements(totalElements);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        handleAxiosErrors(err, "fetch templates");
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    },
    [authToken],
  );

  useEffect(() => {
    fetchTemplates(query);
  }, [query, reloadKey, fetchTemplates]);

  // get by slug (useful for deep-linking straight to a template's detail)
  const getTemplateBySlug = useCallback(
    async (slug) => {
      if (!authToken || !slug) return;

      try {
        const res = await apiClient.get(`${BASE_URL}/${slug}`);
        return formatTemplate(res.data);
      } catch (err) {
        handleAxiosErrors(err, "fetch template by slug");
      }
    },
    [authToken],
  );

  // create
  const createTemplate = useCallback(
    async (payload) => {
      if (!authToken) return;

      try {
        const res = await apiClient.post(BASE_URL, payload);
        toast.success("Template created");
        refresh();
        return formatTemplate(res.data);
      } catch (err) {
        handleAxiosErrors(err, "create template");
        throw err;
      }
    },
    [authToken, refresh],
  );

  // update
  const updateTemplate = useCallback(
    async (slug, payload) => {
      if (!authToken || !slug) return;

      try {
        const res = await apiClient.put(`${BASE_URL}/${slug}`, payload);
        toast.success("Template updated");
        refresh();
        return formatTemplate(res.data);
      } catch (err) {
        handleAxiosErrors(err, "update template");
        throw err;
      }
    },
    [authToken, refresh],
  );

  // soft delete — see TemplateAdminService on the backend for why this
  // deactivates rather than removing the row outright
  const deactivateTemplate = useCallback(
    async (slug) => {
      if (!authToken || !slug) return;

      try {
        await apiClient.delete(`${BASE_URL}/${slug}`);
        toast.success("Template deactivated");
        refresh();
      } catch (err) {
        handleAxiosErrors(err, "deactivate template");
        throw err;
      }
    },
    [authToken, refresh],
  );

  // undo a deactivate
  const reactivateTemplate = useCallback(
    async (slug) => {
      if (!authToken || !slug) return;

      try {
        const res = await apiClient.post(`${BASE_URL}/${slug}/reactivate`);
        toast.success("Template reactivated");
        refresh();
        return formatTemplate(res.data);
      } catch (err) {
        handleAxiosErrors(err, "reactivate template");
        throw err;
      }
    },
    [authToken, refresh],
  );

  return {
    templates,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    query,
    updateQuery,
    refresh,
    getTemplateBySlug,
    createTemplate,
    updateTemplate,
    deactivateTemplate,
    reactivateTemplate,
  };
}
